import { getAllFeatureValues } from '@editor/services/growthbook';
import { useDesignsStore } from '@editor/stores/designsStore';
import { useUserStore } from '@editor/stores/userStore';
import analytics, { AnalyticsData } from '@repo/analytics';
import { User } from '@repo/domain/user';

import customerIoEvents from './customerIoEvents';
import { loggedOutMixpanelEvents } from './mixpanelEvents';
import { userApiEvents } from './userApiEvents';

let removeListener: () => void;

/**
 * Plugin configuration for analytics events
 * Disable plugins by default and enable them based on event type and environment
 * Note: Does not include user-specific configuration
 */
analytics.on('trackStart', ({ payload }: AnalyticsData) => {
  const user = useUserStore.getState().user;

  payload.options = {
    ...payload.options,

    plugins: {
      api: false,
      customerio: false,

      // allow options passed via track call to override defaults
      ...payload.options?.plugins,
    },
  };

  // @TODO: we should do this on each event, not centrally
  // e.g. via analytics.track('event', props, { customerIo: true })
  if (customerIoEvents.includes(payload.event)) {
    payload.options.plugins.customerio = true;
  }

  // @TODO: we should do this on each event, not centrally
  // e.g. via analytics.track('event', props, { api: true })
  if (userApiEvents?.[payload.event]) {
    payload.event = userApiEvents[payload.event];
    payload.options.plugins.api = true;
  }

  if (!!user || loggedOutMixpanelEvents.includes(payload.event)) {
    payload.options.plugins.mixpanel = true;
  }
});

/**
 * userPayloadConfiguration
 * Configure User-specific properties for analytics events
 *
 * @param {Object} payload - analytics event payload
 * @returns void
 */
const userPayloadConfiguration = ({
  payload,
}: {
  payload: Record<string, unknown>;
}): void => {
  // refetch in this scope to avoid stale data
  const user = useUserStore.getState().user;
  const activeDesignId = useDesignsStore.getState().active;
  const props = payload?.properties ?? {};

  if (!user) {
    return;
  }

  payload.properties = {
    country: user.country?.current,
    featureGroups: user.featureGroups,
    featureFlags: getAllFeatureValues(),
    designId: activeDesignId,
    ...props,
  };
};

/**
 * identifyAnalytics
 * Identify user for analytics events and configure user-specific properties
 * @param user
 */
export async function identifyAnalytics(user: User | null): Promise<void> {
  // Early return if running in a test environment or if localStorage is not available
  if (typeof localStorage === 'undefined') {
    console.warn(
      'Analytics: localStorage is not available, skipping user identification'
    );
    return;
  }

  if (user) {
    // remove previous listener to avoid duplicate events
    removeListener?.();

    removeListener = analytics.on('trackStart', userPayloadConfiguration);
    await analytics.identify(user.id, {
      email: user.email,
      plan: user.plan,
      id: user.id,
    });
  }
}

/**
 * resetAnalytics
 * Reset analytics state and remove listeners
 * @returns void
 */
export async function resetAnalytics(): Promise<void> {
  await analytics.reset();
  removeListener?.();
}

export default analytics;
